// Shared types, palette, illustration catalog, and URL helpers for the
// brochure-builder sub-components. Kept in one non-React module so the sibling
// section files don't circular-import each other (same pattern as the
// webinar-builder's stock-images.ts).

export interface BrochureFeature {
  id: string
  // The "illustration" is an emoji glyph. Emoji is the ONLY illustration
  // primitive that survives every mail client: it's Unicode text (can't be
  // image-blocked or stripped like an <svg>/icon-font), renders in full color in
  // Gmail / Apple Mail / Outlook-for-Mac and as a monochrome-but-present glyph in
  // Outlook-for-Windows. See the module doc-comment in html-helpers.ts.
  emoji: string
  title: string
  text: string
}

export type BrochureSectionType = 'hero' | 'highlights' | 'showcase' | 'cta' | 'footer'

export interface BrochureSection {
  id: string
  type: BrochureSectionType
  content: any
  backgroundColor?: string
}

// One shared brand palette for the whole brochure (navy + lime, matching the
// app theme's primary/secondary). Every section + the document scaffold pulls
// its colors from here so a re-skin is a one-file edit. sRGB hex only — email
// clients don't do CSS variables / OKLCH.
export const BROCHURE_COLORS = {
  navy: '#16233D',      // hero / CTA band background, headings
  navyDeep: '#101B30',  // darker navy for the top brand strip
  lime: '#B4D335',      // accent: eyebrow, buttons, rules
  ink: '#1F2937',       // body text
  muted: '#5B6472',     // secondary text
  soft: '#F5F7FB',      // page background + feature-card surface
  card: '#FFFFFF',
  border: '#E4E8F0',
  white: '#FFFFFF',
} as const

// The curated emoji palette offered in the feature-card icon picker. Kept small
// and business-appropriate so the brochure stays on-brand.
export const FEATURE_EMOJI_CHOICES = [
  '🚀', '💡', '📈', '🔒', '⚡', '🌐', '🤝', '🎯',
  '📞', '🛡️', '⭐', '🔔', '☁️', '🧩', '📊', '✅',
]

// The XOP brand mark ("the ball" — navy + lime orbit, NO wordmark). Rendered in
// the hero brand strip + the tenant name as text, so the lockup reads
// [ball] XOP Networks exactly once (mirrors the webinar / olive builders, which
// fixed the duplicated-wordmark bug). PNG (mail clients drop SVG); CID-inlined
// at send by EmailImageInliner like every other brochure image.
// Asset: xop-edge-customer/apps/portal-web/public/stock-images/xop-ball.png.
export const XOP_BRAND_MARK_URL = '/stock-images/xop-ball.png'

// Reuse the webinar builder's professional stock-photo catalog for the showcase
// image picker rather than duplicating it — one source of truth for the images
// bundled on the public host. Re-exported under a brochure-friendly name.
export { headerImageOptions as showcaseImageOptions } from '@crm-src/components/webinar-builder/stock-images'
export type { TenantInfo } from '@crm-src/components/webinar-builder/stock-images'

// Anchor a relative asset URL to a public host — same two helpers/call-sites as
// the webinar builder:
//   * toAbsoluteUrl     → the SENT-EMAIL *ToHtml serializers (always the public
//                          support host, where own images are CID-inlined).
//   * toPreviewImageUrl → the in-EDITOR React <img> previews.
// Both re-exported from the single source of truth (crm/lib/public-url.ts).
export { toAbsoluteUrl, toPreviewImageUrl } from '@crm-src/lib/public-url'
